import { fromJS, List, Map } from 'immutable';
import { createSelector } from 'reselect';

import config from '../../config';
import * as ActionType from '../../actions/contactDetail';
import * as ActionExecType from '../../actions/contact/contactExecutive';
import * as ActionLpType from '../../actions/contact/contactLP';
import getValidator from '../../utils/getValidator';
import handleApiError, { withFieldPrefix } from '../decorators/handleApiError';
import { spy, setIn, unwrap, toJS } from '../../utils/ChangeSpy';
import { getAge, getBirthYear } from '../../utils/yearAgeConverter';

const SCHEMA = {
  type: 'object',
  properties: {
    fullName: {
      type: 'string',
      title: 'Full Name',
      required: true,
      minLength: 1,
      maxLength: 100,
    },
    firstName: {
      type: 'string',
      title: 'First Name',
      required: true,
      minLength: 1,
      maxLength: 49,
    },
    lastName: {
      type: 'string',
      title: 'Last Name',
      required: true,
      minLength: 1,
      maxLength: 49,
    },
    linkedin: {
      title: 'LinkedIn',
      description: 'should be a URL string or empty',
      oneOf: [
        { type: 'string', format: 'uri' },
        { type: 'string', maxLength: 0 },
      ],
    },
    age: {
      title: 'Age',
      description: 'should be empty or positive integer less than 200',
      type: 'string',
      minLength: 0,
      maxLength: 3,
      pattern: /^[12]?[1234567890]{0,2}$/,
    },
    pronounce: {
      title: 'Pronounce',
      type: 'string',
      maxLength: 49,
    },
    phone: config.phoneValidation,
    phones: {
      type: 'array',
      items: {
        type: 'object',
        properties: config.phoneValidation,
      },
    },
    email: {
      title: 'E-mail',
      description: 'should be empty or valid e-mail address',
      oneOf: [
        { type: 'string', format: 'email' },
        { type: 'string', maxLength: 0 },
      ],
    },
    emails: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          value: {
            title: 'E-mail',
            description: 'should be a valid e-mail',
            type: 'string',
            format: 'email',
          },
        },
      },
    },
  },
};

const validator = getValidator(SCHEMA);

const VALUES_TO_SPY = [
  'firstName',
  'lastName',
  'prefix',
  'suffix',
  'fullName',
  'doNotMail',
  'nick',
  'pronounce',
  'linkedin',
  'age',
  'birthYear',
  'email',
];
const EMAIL_VALUES_TO_SPY = ['value', 'type'];
const PHONE_VALUES_TO_SPY = [...EMAIL_VALUES_TO_SPY];
const ADDRESS_VALUES_TO_SPY = [...EMAIL_VALUES_TO_SPY, 'address', 'city', 'zip', 'countryName', 'state', 'showCountry'];

const prefixes = config.values.getIn(['contact', 'prefixes']);
const suffixes = config.values.getIn(['contact', 'suffixes']);
const states = config.company.states.reduce((ret, st) => {
  if (st) {
    ret.push(Map({ name: st, value: st }));
  }

  return ret;
}, []);

const checkValue = value => valueObject => valueObject.get('value') === value;

const defaultState = fromJS(
  spy(
    {
      email: '',
      prefix: prefixes.get(0),
      prefixes,
      fullName: '',
      suffix: suffixes.get(0),
      suffixes,
      firstName: '',
      lastName: '',
      nick: '',
      pronounce: '',
      linkedin: '',
      birthYear: null,
      age: '',
      doNotMail: false,

      loading: true,
      hasTargets: false,
      hasBuyers: false,
      isExecutive: false,
      isLP: false,

      phones: [],
      emails: [],
      addresses: [],
      states,
      editing: [],
      inputError: {},
      inputErrors: {},
    },
    VALUES_TO_SPY,
  ),
);

function changeField(state, { name, value }) {
  switch (name) {
    case 'prefix': {
      return setIn(state, name, prefixes.find(checkValue(value), null, prefixes.get(0)));
    }

    case 'age': {
      return state.mergeDeep({
        age: { value },
        birthYear: { value: getBirthYear(value) },
      });
    }

    default:
      return setIn(state, name, value);
  }
}

function mapResponse({ data }) {
  const {
    id,
    fullName,
    firstName,
    lastName,
    doNotMail,
    nick,
    pronounce,
    linkedin,
    birthYear,

    hasTargets,
    hasBuyers,
    recordOwnerId,
    lpPrimaryRelId,

    phones,
    emails,
    addresses,
  } = data;
  let { prefix, suffix } = data;

  prefix = prefixes.find(checkValue(prefix), null, prefixes.get(0));
  suffix = suffixes.find(checkValue(suffix), null, fromJS({ name: suffix, value: suffix }));

  return spy(
    {
      id,
      prefix,
      firstName,
      lastName,
      suffix,
      fullName,
      doNotMail,
      nick,
      pronounce,
      linkedin,
      age: getAge(birthYear),
      birthYear: birthYear || null,

      hasTargets,
      hasBuyers,
      isLP: !!lpPrimaryRelId,
      isExecutive: !!recordOwnerId,

      editing: [],

      phones: phones.map(mapPhone),
      emails: emails.map(mapEmail),
      addresses: addresses.map(mapAddress),
    },
    VALUES_TO_SPY,
  );
}

function mapAddress(rawAddress) {
  const {
    addressInherited: isInherited = false,
    entityId: companyId = 0,
    legalName: companyName = '',
    address,
    id,
    type,
    city,
    state,
    zip,
    countryName,
    timezone,
  } = rawAddress;

  return spy(
    {
      isInherited,
      companyId,
      companyName,
      showCountry: !!countryName,
      value: getFullAddress(rawAddress),
      address,
      id,
      type,
      city,
      zip,
      countryName,
      state,
      timezone,
    },
    ADDRESS_VALUES_TO_SPY,
  );
}

function mapEmail({ id, type, email: value, isVerify, contactId, deletedAt, priority }) {
  return spy({ id, type, value, isVerify, contactId, deletedAt, priority }, EMAIL_VALUES_TO_SPY);
}

function mapPhone(rawPhone) {
  const {
    phone: value,
    id,
    type,
    phoneInherited: isInherited = false,
    entityId: companyId = 0,
    legalName: companyName = '',
    contactId,
    priority,
  } = rawPhone;

  return spy(
    {
      id,
      isInherited,
      companyId,
      companyName,
      type,
      value,
      contactId,
      priority,
    },
    PHONE_VALUES_TO_SPY,
  );
}

let wrappedReducer = reducer;

wrappedReducer = handleApiError(ActionExecType.MAKE_ERROR_CONTACT_EXECUTIVE, wrappedReducer);
wrappedReducer = handleApiError(ActionLpType.MAKE_ERROR_CONTACT_LP, wrappedReducer);
wrappedReducer = handleApiError(ActionType.ERROR_SAVE_CONTACT_DETAIL, wrappedReducer);
wrappedReducer = withFieldPrefix(ActionType.ERRORED_SAVE_CONTACT_PHONE, wrappedReducer, fieldGetter('phones'));
wrappedReducer = withFieldPrefix(ActionType.ERRORED_SAVE_CONTACT_EMAIL, wrappedReducer, fieldGetter('emails'));
wrappedReducer = withFieldPrefix(ActionType.ERRORED_SAVE_CONTACT_ADDRESS, wrappedReducer, fieldGetter('addresses'));

function fieldGetter(type) {
  function find(state, type, id) {
    return state.get(type).findKey(ent => ent.get('id') === id);
  }

  return (state, action, errors) => {
    const path = [type, find(state, type, action.id), 'value'].join('.');
    const result = {};

    Object.keys(errors).forEach(key => {
      result[path] = errors[key];
    });

    return result;
  };
}

export default wrappedReducer;

function reducer(state = defaultState, action) {
  switch (action.type) {
    case ActionType.CREATE_CONTACT:
      return checkValidity(defaultState.merge({ loading: false }));

    case ActionType.CHANGE_CONTACT_INFO_FIELD:
      return checkValidity(changeField(state, action));

    case ActionType.LOAD_CONTACT_DETAIL:
      return defaultState;

    case ActionType.LOADED_CONTACT_DETAIL:
      return state.merge(mapResponse(action.response)).set('loading', false);

    case ActionType.ERROR_CONTACT_DETAIL:
      return state.set('loading', false);

    case ActionType.ADD_CONTACT_FIELD:
      return state.update(action.field, values => {
        const newItem = fromJS(
          spy({ value: '', id: 'new', type: '', priority: values.size, isNewItem: true }, [
            ...EMAIL_VALUES_TO_SPY,
            ...ADDRESS_VALUES_TO_SPY,
            ...PHONE_VALUES_TO_SPY,
          ]),
        );

        // Find the index of the first inherited item
        const inheritedIndex = values.findIndex(item => item.get('isInherited'));

        // If there are inherited items, insert before them; otherwise, append
        if (inheritedIndex !== -1 && action.field === 'phones') {
          return values.insert(inheritedIndex, newItem);
        }

        return values.push(newItem);
      });

    case ActionType.SAVE_CONTACT_FIELD:
      return state.delete('_tempData');

    case ActionType.REVERT_CONTACT_FIELD: {
      if (!state.has('_tempData')) return state;

      return state.delete('_tempData').setIn(state.get('editing'), state.get('_tempData'));
    }

    case ActionType.EDIT_CONTACT_FIELD:
      return state
        .set('editing', List([action.field, action.index]))
        .deleteIn(state.get('editing').push('isEditing'))
        .setIn([action.field, action.index, 'isEditing'], true)
        .set('_tempData', state.getIn([action.field, action.index]));

    case ActionType.SAVED_CONTACT_DETAIL:
      return spy(
        action.fields.reduce((result, field) => result.set(field, unwrap(result.get(field))), state),
        VALUES_TO_SPY,
      );

    case ActionType.SAVED_CONTACT_PHONE: {
      const nextState = state.delete('_tempData');

      return nextState.update('phones', phones =>
        phones.map(phone =>
          phone.get('id') !== action.id
            ? phone
            : spy(
                setEntityId(
                  action.fields.reduce((result, field) => result.set(field, unwrap(result.get(field))), phone),
                  action,
                ),
                PHONE_VALUES_TO_SPY,
              ),
        ),
      );
    }

    case ActionType.SAVED_CONTACT_EMAIL:
      return state.update('emails', emails =>
        emails.map(email =>
          email.get('id') !== action.id
            ? email
            : spy(
                setEntityId(
                  action.fields.reduce((result, field) => result.set(field, unwrap(result.get(field))), email),
                  action,
                ),
                EMAIL_VALUES_TO_SPY,
              ),
        ),
      );

    case ActionType.SAVED_CONTACT_ADDRESS:
      return state.update('addresses', addresses =>
        addresses.map(address =>
          address.get('id') !== action.id
            ? address
            : spy(
                setEntityId(
                  action.fields.reduce((result, field) => result.set(field, unwrap(result.get(field))), address),
                  action,
                ),
                ADDRESS_VALUES_TO_SPY,
              ),
        ),
      );

    case ActionType.STOP_EDIT_CONTACT_FIELD:
      return state.set('editing', List([])).deleteIn(state.get('editing').push('isEditing'));

    case ActionType.CHANGE_CONTACT_FIELD:
      if (state.getIn(['editing', 0], null) === 'addresses') {
        return checkValidity(changeAddress(state, action));
      }

      if (action.field === 'emails' && action.name === 'value' && action.value) {
        action.value = action.value.trim();
      }

      return checkValidity(setIn(state, state.get('editing').push(action.name), action.value));

    case ActionType.DELETE_CONTACT_FIELD:
      return state.deleteIn([action.field, action.index]);

    case ActionType.REORDER_CONTACT_FIELD: {
      const { field, oldIndex, newIndex } = action;

      return state.update(field, list => {
        const item = list.get(oldIndex);

        return list.delete(oldIndex).insert(newIndex, item);
      });
    }

    case ActionType.CLOSE_CONTACT_VALIDATION_ERROR:
      return state.deleteIn(['inputErrors', action.field]);

    case ActionType.SET_NAME_FIELD_ERROR:
      return setIn(
        state.set('inputErrors', state.get('inputErrors', Map()).set(action.field, action.value)),
        action.field,
        '',
      );

    default:
      return state;
  }
}

function changeAddress(state, action) {
  let nextState = state.setIn(state.get('editing').push(action.name, 'value'), action.value);

  nextState = checkCountryAndZip(nextState, action);

  const address = nextState.getIn(state.get('editing'));
  const value = getFullAddress(address);

  return nextState.setIn(state.get('editing').push('value', 'value'), value);
}

function checkCountryAndZip(state, action) {
  if (action.name === 'showCountry') {
    if (action.value) return state.setIn(state.get('editing').push('zip', 'value'), '');

    return state.setIn(state.get('editing').push('countryName', 'value'), '');
  }

  return state;
}

function safe(value) {
  return String(value || '').trim();
}

function getFullAddress(contactAddress) {
  if (Map.isMap(contactAddress)) {
    const countryName = safe(contactAddress.getIn(['countryName', 'value'], ''));
    const state = safe(contactAddress.getIn(['state', 'value'], ''));
    const zip = safe(contactAddress.getIn(['zip', 'value'], ''));
    const city = safe(contactAddress.getIn(['city', 'value'], ''));
    const address = safe(contactAddress.getIn(['address', 'value'], ''));

    return [address, city, safe(`${state} ${zip}`), countryName].filter(v => v && v.length > 0).join(', ');
  }

  const countryName = safe(contactAddress.countryName);
  const state = safe(contactAddress.state);
  const zip = safe(contactAddress.zip);
  const city = safe(contactAddress.city);
  const address = safe(contactAddress.address);

  return [address, city, safe(`${state} ${zip}`), countryName].filter(v => v && v.length > 0).join(', ');
}

const toJSSelector = createSelector(
  state => state,
  state => toJS(state),
);

function checkValidity(state) {
  const plainState = toJSSelector(state);

  const isValid = validator.isValid(plainState);

  const errors = validator.getFirstError(plainState);

  Object.keys(errors).forEach(key => {
    let newKey;

    if (/^addresses\.\d+\./.test(key)) {
      const keyParts = key.split('.');

      keyParts.pop();
      keyParts.push('value');
      newKey = keyParts.join('.');
    }
    if (!newKey) return;
    errors[newKey] = errors[key];
  });

  return state.merge({
    inputErrors: errors,
    isValid,
  });
}

function setEntityId(ent, action) {
  const id = ent.get('id');

  if (parseInt(id, 10)) return ent;
  if (action && action.response && action.response.id) {
    ent = ent.set('id', action.response.id);
  }

  return ent;
}
